import { useState } from "react";
import { ShareNetwork, CheckCircle, Warning, ArrowClockwise } from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SocialCachePanel({ token, setError }) {
  const [state, setState] = useState("idle"); // idle | running | done
  const [results, setResults] = useState(null);

  const flush = async () => {
    setState("running");
    setResults(null);
    try {
      const r = await fetch(`${BACKEND}/api/admin/social-cache/flush`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Token": token },
        body: JSON.stringify({}),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setResults(data);
      setState("done");
    } catch (e) {
      setError(`Cache flush failed: ${e?.message || "network error"}`);
      setState("idle");
    }
  };

  const flushBtn = (
    <button
      data-testid="social-cache-flush"
      onClick={(e) => { e.stopPropagation(); flush(); }}
      disabled={state === "running"}
      className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] border border-[#FF1B91] text-[#FF1B91] hover:bg-[#FF1B91]/10 disabled:opacity-50 px-3 py-2 font-bold"
    >
      <ArrowClockwise size={12} weight="bold" className={state === "running" ? "animate-spin" : ""} />
      {state === "running" ? "Flushing…" : "Flush all"}
    </button>
  );

  return (
    <CollapsibleSection
      id="social-cache"
      testId="admin-social-cache"
      title={<><ShareNetwork size={18} weight="fill" className="text-[#FF1B91] inline mr-2" />Social Preview Cache</>}
      subtitle="Force Facebook · iMessage · WhatsApp · Instagram DM link previews to refresh."
      extraActions={flushBtn}
      defaultOpen={false}
    >
      <p className="text-white/50 text-xs leading-relaxed mb-4">
        Forces Facebook, iMessage, WhatsApp, and Instagram DM link-previews to refresh with your latest branding. Run this after every deploy that changes the logo, OG image, page title, or description.
      </p>

      {results && (
        <div className="border border-white/10 bg-[#0A0A0A] p-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2 flex justify-between">
            <span>{results.ok_count} of {results.total} URLs refreshed</span>
          </div>
          <div className="space-y-1.5">
            {results.results.map((r) => (
              <div key={r.url} className="text-xs flex items-start gap-2 py-1 border-b border-white/5 last:border-b-0" data-testid={`cache-result-${r.url}`}>
                {r.ok ? (
                  <CheckCircle size={14} weight="fill" className="text-[#22C55E] shrink-0 mt-0.5" />
                ) : (
                  <Warning size={14} weight="fill" className="text-yellow-400 shrink-0 mt-0.5" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-white/85 truncate">{r.url}</div>
                  {r.ok && r.title && <div className="text-white/50 text-[11px] truncate">{r.title}</div>}
                  {r.error && <div className="text-yellow-400 text-[11px]">{r.error}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
}
